import { inject, observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import withAuth from '../../HOC/withAuth';
import CustomPagination from '../../components/Pagination';
import Empty from '../../components/Empty';
import C from '../../colors';

function Icon({ name, size = 16 }) {
  const s = { width: size, height: size, fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'bell': return <svg viewBox="0 0 24 24" {...s}><path d="M18 9a6 6 0 0 0-12 0c0 7-3 7-3 7h18s-3 0-3-7M10 20a2 2 0 0 0 4 0"/></svg>;
    case 'check': return <svg viewBox="0 0 24 24" {...s}><path d="m5 12 5 5L20 7"/></svg>;
    default: return null;
  }
}

function getTotalPages(pagination) {
  return pagination?.totalPages || pagination?.totalPage || pagination?.pages || 1;
}

function normalizeData(data) {
  if (!data) return {};
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (e) {
      return {};
    }
  }
  return data;
}

const Notifications = inject('userStore', 'snackbarStore')(observer(({ userStore, snackbarStore }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(false);

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [notifications]);

  const fetchNotifications = useCallback(() => {
    setLoading(true);
    userStore.fetchNotifications({ page, limit: 20, unreadOnly })
      .then(res => {
        setNotifications(res.notifications || []);
        setPagination(res.pagination || null);
      })
      .catch(() => {
        setNotifications([]);
      })
      .finally(() => setLoading(false));
  }, [page, unreadOnly, userStore]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    const timer = setInterval(() => {
      userStore.fetchUnreadNotifications().catch(() => {});
    }, 30000);
    return () => clearInterval(timer);
  }, [userStore]);

  const handleUnreadToggle = () => {
    setPage(1);
    setUnreadOnly(v => !v);
  };

  const markAllRead = () => {
    userStore.markAllNotificationsRead()
      .then(() => {
        setNotifications(list => (
          unreadOnly
            ? []
            : list.map(item => ({ ...item, isRead: true, readAt: item.readAt || new Date().toISOString() }))
        ));
        snackbarStore.showSnackbar('已全部标记为已读');
      })
      .catch(() => {});
  };

  const handleNotificationClick = (notification) => {
    const data = normalizeData(notification.data);
    const markRead = notification.isRead
      ? Promise.resolve()
      : userStore.markNotificationRead(notification.id).then(() => {
          setNotifications(list => (
            unreadOnly
              ? list.filter(item => item.id !== notification.id)
              : list.map(item => (
                  item.id === notification.id
                    ? { ...item, isRead: true, readAt: item.readAt || new Date().toISOString() }
                    : item
                ))
          ));
        });

    markRead.then(() => {
      if (notification.type === 'selection_submitted') {
        if (data.orderId) {
          navigate(`/user/orders/${data.orderId}/step5`);
        }
      }
    }).catch(() => {});
  };

  const totalPages = getTotalPages(pagination);
  const unreadCount = userStore.unreadNotifications || 0;

  return (
    <div style={{ minHeight: '100%', background: C.paper }}>
      <header style={{
        padding: '32px 40px 24px',
        borderBottom: `1px solid ${C.hair}`,
        background: C.paper,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontFamily: C.mono, fontSize: 11, letterSpacing: 1.4,
              color: C.brand2, textTransform: 'uppercase', fontWeight: 600,
              marginBottom: 8,
            }}>
              <Icon name="bell" size={14}/>
              Workspace · Notifications
            </div>
            <h1 style={{ margin: 0, fontFamily: C.serif, fontSize: 42, fontWeight: 400, lineHeight: 1.02 }}>
              通知
              <span style={{ color: C.ink3, fontSize: 22, marginLeft: 14 }}>
                · {unreadCount} 条未读
              </span>
            </h1>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={handleUnreadToggle} style={{
              fontFamily: C.sans, fontSize: 13, fontWeight: 500,
              padding: '8px 12px', borderRadius: 7,
              background: unreadOnly ? C.ink : '#fff',
              color: unreadOnly ? '#fff' : C.ink,
              border: `1px solid ${unreadOnly ? C.ink : C.hair2}`,
              cursor: 'pointer',
            }}>
              {unreadOnly ? '查看全部' : '只看未读'}
            </button>
            <button onClick={markAllRead} disabled={unreadCount <= 0} style={{
              fontFamily: C.sans, fontSize: 13, fontWeight: 500,
              padding: '8px 12px', borderRadius: 7,
              background: unreadCount > 0 ? C.brand : C.hair2,
              color: '#fff', border: 'none',
              cursor: unreadCount > 0 ? 'pointer' : 'not-allowed',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              <Icon name="check" size={14}/>
              全部标记已读
            </button>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 32px 48px' }}>
        <div style={{
          background: '#fff',
          border: `1px solid ${C.hair}`,
          borderRadius: 12,
          overflow: 'hidden',
        }}>
          {loading && (
            <div style={{ padding: 36, textAlign: 'center', color: C.ink3, fontSize: 14 }}>加载中…</div>
          )}

          {!loading && sortedNotifications.length === 0 && (
            <div style={{ padding: 36 }}>
              <Empty message={unreadOnly ? '暂无未读通知' : '暂无通知'}/>
            </div>
          )}

          {!loading && sortedNotifications.map(notification => (
            <button
              key={notification.id}
              data-testid={`notification-${notification.id}`}
              data-notification-type={notification.type || ''}
              data-read={notification.isRead ? 'true' : 'false'}
              onClick={() => handleNotificationClick(notification)}
              style={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto',
                gap: 14,
                padding: '18px 20px',
                border: 'none',
                borderBottom: `1px solid ${C.hair}`,
                background: notification.isRead ? '#fff' : C.paper,
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: C.sans,
              }}
            >
              <span style={{
                width: 9, height: 9, borderRadius: 5, marginTop: 8,
                background: notification.isRead ? C.hair2 : C.brand,
              }}/>
              <span style={{ minWidth: 0 }}>
                <span style={{
                  display: 'block',
                  fontSize: 15,
                  fontWeight: notification.isRead ? 500 : 700,
                  color: notification.isRead ? C.ink2 : C.ink,
                  marginBottom: 6,
                }}>
                  {notification.title || '系统通知'}
                </span>
                <span style={{
                  display: 'block',
                  fontSize: 13,
                  lineHeight: 1.65,
                  color: notification.isRead ? C.ink3 : C.ink2,
                }}>
                  {notification.content}
                </span>
              </span>
              <span style={{
                fontFamily: C.mono,
                fontSize: 11,
                color: C.ink3,
                whiteSpace: 'nowrap',
                marginTop: 2,
              }}>
                {notification.createdAt ? dayjs(notification.createdAt).format('YYYY-MM-DD HH:mm') : ''}
              </span>
            </button>
          ))}
        </div>

        <div style={{ marginTop: 18 }}>
          <CustomPagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
          />
        </div>
      </div>
    </div>
  );
}));

export default withAuth(Notifications);
